import { useEffect, useRef, useCallback } from "react";

export default function useIdleNudge(idleDelay = 10000) {
  const targetRef = useRef(null);
  const animationRef = useRef(null);
  const timerRef = useRef(null);

  const startNudge = useCallback(() => {
    if (!targetRef.current || animationRef.current) return;

    const DIST = 6;
    const DURATION = 400;

    animationRef.current = targetRef.current.animate(
      [
        { transform: "translateY(0)" },
        { transform: `translateY(${DIST}px)` },
        { transform: `translateY(-${DIST}px)` },
        { transform: "translateY(0)" },
      ],
      { duration: DURATION, easing: "ease-in-out" }
    );

    animationRef.current.onfinish = () => {
      animationRef.current = null;
      resetTimer();
    };
  }, []);

  const resetTimer = useCallback(() => {
    if (animationRef.current) {
      animationRef.current.cancel();
      animationRef.current = null;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(startNudge, idleDelay);
  }, [idleDelay, startNudge]);

  useEffect(() => {
    const events = ["scroll", "click", "mousemove", "keydown"];

    // Attach listeners to window to detect global inactivity
    events.forEach((evt) =>
      window.addEventListener(evt, resetTimer, { passive: true })
    );

    // Initial countdown
    timerRef.current = setTimeout(startNudge, idleDelay);

    return () => {
      clearTimeout(timerRef.current);
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
      if (animationRef.current) animationRef.current.cancel();
    };
  }, [resetTimer, idleDelay, startNudge]);

  // Return the ref to be attached to any element
  return targetRef;
}
